import { createFileRoute } from "@tanstack/react-router";
import { SEOPage } from "@/components/site/SEOPage";
import { Check, List, Wifi, Signal, Globe, Server, Settings, Zap, ShieldAlert, Activity, Search, Info, Tv, HardDrive, Smartphone, Radio, Satellite } from "lucide-react";

const ArticlePage = () => {
  const breadcrumbs = [
    { label: "Elite", href: "/" },
    { label: "Blog", href: "/blog" },
    { label: "IPTV Precisa de Antena?", href: "/blog/iptv-precisa-de-antena" }
  ];

  const faq = [
    {
      question: "Dá para usar IPTV com antena parabólica?",
      answer: "Não. O IPTV funciona 100% via internet. Se você tem uma parabólica, ela é desnecessária para o funcionamento da P2 PRO. O sinal de IPTV chega pelo seu cabo de rede ou Wi-Fi."
    },
    {
      question: "Preciso de antena digital para os canais abertos?",
      answer: "No IPTV da P2 PRO, todos os canais abertos (Globo, SBT, Record, etc.) já estão inclusos na grade via streaming. Você não precisa de antena digital UHF para assisti-los."
    },
    {
      question: "IPTV é o mesmo que Sky Gato ou receptores de antena?",
      answer: "Não. Os receptores de antena (SKS/IKS) dependem de duas antenas e cabos coaxiais. O IPTV é uma tecnologia moderna que elimina toda essa fiação, dependendo apenas de uma boa conexão de internet."
    },
    {
      question: "O IPTV funciona em lugares sem sinal de TV aberta?",
      answer: "Sim! Desde que haja internet (via fibra, rádio ou até satélite como a Starlink), o IPTV funcionará perfeitamente, mesmo em zonas rurais onde as antenas comuns não pegam."
    }
  ];

  const benefits = [
    {
      title: "Zero Fiação Externa",
      description: "Livre-se de antenas no telhado, cabos coaxiais furando paredes e divisores de sinal.",
      icon: <Radio className="w-6 h-6" />
    },
    {
      title: "Instalação Instantânea",
      description: "Ative sua TV completa em minutos apenas baixando um aplicativo, sem precisar de técnico.",
      icon: <Zap className="w-6 h-6" />
    },
    {
      title: "Imunidade Climática",
      description: "Diferente da antena, o IPTV não 'sai do ar' ou fica com chuvisco quando chove forte.",
      icon: <ShieldAlert className="w-6 h-6" />
    }
  ];

  const howItWorks = [
    {
      title: "Recepção de Dados",
      description: "O sinal é enviado codificado pelos nossos servidores globais via fibra óptica."
    },
    {
      title: "Decodificação Lógica",
      description: "Seu aplicativo (One X / P2 PLAY) traduz os pacotes de dados em imagem 4K."
    },
    {
      title: "Distribuição Interna",
      description: "A internet distribui o sinal para qualquer tela da casa simultaneamente."
    },
    {
      title: "Controle Total",
      description: "Você gerencia canais e VODs sem depender da posição de satélites."
    }
  ];

  const diferenciais = [
    {
      title: "Cloud Signaling",
      description: "Nossa tecnologia de nuvem substitui o hardware físico das antenas tradicionais.",
      icon: <Globe className="w-6 h-6" />
    },
    {
      title: "Adaptive Streaming",
      description: "O sinal se adapta à sua banda, algo impossível de fazer com recepção via antena.",
      icon: <Activity className="w-6 h-6" />
    }
  ];

  const sections = [
    {
      title: "1. Introdução: O Fim da Era das Antenas",
      content: (
        <div className="prose prose-invert max-w-none">
          <p className="text-lg leading-relaxed text-muted-foreground mb-6">
            O conceito de televisão mudou. Durante décadas, estivemos presos a estruturas físicas pesadas: antenas gigantes no telhado, cabos coaxiais grossos e a necessidade constante de 'apontamento' para satélites. Em 2026, a <strong>P2 PRO</strong> lidera a transição definitiva para o streaming de elite. IPTV precisa de antena? A resposta técnica é um sonoro <strong>não</strong>. O IPTV (Internet Protocol Television) substitui a antena física pela 'estrada' lógica da internet.
          </p>
          <p className="text-lg leading-relaxed text-muted-foreground mb-6">
            Neste guia denso, explicamos por que o IPTV é superior às tecnologias de antena (DTH, UHF e Satélite). Mostramos como economizar dinheiro com manutenção física e como garantir que sua 'antena digital invisível' (seu roteador) esteja pronta para o sinal 4K.
          </p>
        </div>
      )
    },
    {
      title: "2. IPTV vs Tecnologias de Antena: O Comparativo",
      type: "featured" as const,
      content: (
        <div className="space-y-8">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse bg-white/[0.02] border border-white/10 rounded-2xl">
              <thead>
                <tr className="border-b border-white/10 bg-white/[0.05]">
                  <th className="p-4 text-left font-black uppercase tracking-tighter italic">Recurso</th>
                  <th className="p-4 text-left font-black uppercase tracking-tighter italic text-electric">IPTV (Elite)</th>
                  <th className="p-4 text-left font-black uppercase tracking-tighter italic">Antena Digital/Sat</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {[
                  { r: "Instalação", pos: "Aplicativo (5 min)", pre: "Técnico e Cabos (2h+)" },
                  { r: "Canais", pos: "100k+ (Global)", pre: "Limitado ao Satélite" },
                  { r: "Chuva/Vento", pos: "Não Afeta (Fibra)", pre: "Sinal Cai ou 'Quadradinhos'" },
                  { r: "Mobilidade", pos: "Assiste onde quiser", pre: "Preso à tomada de antena" },
                  { r: "Qualidade", pos: "4K Real / HDR", pre: "Geralmente HD/FHD" }
                ].map((row, i) => (
                  <tr key={i} className="hover:bg-white/[0.01]">
                    <td className="p-4 text-white font-bold">{row.r}</td>
                    <td className="p-4 text-electric font-black">{row.pos}</td>
                    <td className="p-4 text-muted-foreground">{row.pre}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )
    },
    {
      title: "3. Por que o IPTV não sofre com o 'Mal do Tempo'?",
      content: (
        <div className="prose prose-invert max-w-none space-y-8">
          <p className="text-muted-foreground">Quem usa antena sabe: começou a trovejar, o sinal começa a falhar. Entenda por que no IPTV da P2 PRO isso não acontece:</p>
          <div className="flex gap-6 items-start">
            <div className="w-12 h-12 rounded-2xl bg-electric/20 text-electric flex items-center justify-center flex-shrink-0 font-black italic"><Satellite /></div>
            <div>
              <h4 className="text-white text-xl font-bold mb-2">Transmissão Subterrânea (Fibra)</h4>
              <p className="text-muted-foreground">O sinal do nosso servidor chega à sua casa via cabos de fibra óptica enterrados. Diferente das micro-ondas do satélite, que são bloqueadas por nuvens carregadas de água, a luz dentro da fibra não sofre interferência climática.</p>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "4. Auditoria de Setup: O que substitui a Antena?",
      content: (
        <div className="prose prose-invert max-w-none">
          <p className="text-muted-foreground mb-6">Para trocar sua antena pela P2 PRO com perfeição, foque nestes 3 componentes de rede:</p>
          <ul className="space-y-4">
            <li className="flex items-start gap-4">
              <Check className="w-6 h-6 text-electric mt-1 flex-shrink-0" />
              <div>
                <h5 className="font-bold text-white">Roteador Dual-Band (Wi-Fi 5 ou 6)</h5>
                <p className="text-sm text-muted-foreground">Ele é a sua nova 'antena interna'. Deve ser capaz de transmitir em 5GHz para não travar no 4K.</p>
              </div>
            </li>
            <li className="flex items-start gap-4">
              <Check className="w-6 h-6 text-electric mt-1 flex-shrink-0" />
              <div>
                <h5 className="font-bold text-white">Cabo de Rede Cat6</h5>
                <p className="text-sm text-muted-foreground">Substitua o cabo coaxial antigo por um cabo de rede azul (Cat6) direto na sua TV para estabilidade absoluta.</p>
              </div>
            </li>
            <li className="flex items-start gap-4">
              <Info className="w-6 h-6 text-amber-500 mt-1 flex-shrink-0" />
              <div>
                <h5 className="font-bold text-white">Dica do Instalador Master</h5>
                <p className="text-sm text-muted-foreground">Se você mora em condomínio e não pode furar paredes para passar cabo, use um adaptador Powerline. Ele usa a fiação elétrica da sua casa para levar a internet até a TV, servindo como uma 'antena invisível' perfeita.</p>
              </div>
            </li>
          </ul>
        </div>
      )
    }
  ];

  return (
    <div className="relative">
      <SEOPage
        category="Central de Ajuda IPTV"
        title="IPTV Precisa de Antena? O Guia do Hardware em 2026"
        subtitle="Esqueça os cabos coaxiais e as parabólicas: saiba por que o IPTV é a solução mais limpa e moderna do mercado."
        heroImage="https://images.unsplash.com/photo-1551434678-e076c223a692?auto=format&fit=crop&q=80&w=1200"
        introParagraphs={[
          "A imagem de uma antena parabólica gigante no telhado está se tornando uma peça de museu. Em 2026, a elite do entretenimento não depende mais de sinais analógicos ou de satélites sujeitos a interferências climáticas. O IPTV da P2 PRO utiliza a 'estrada' mais moderna que existe: a fibra óptica e a rede mundial de computadores. Mas será que você realmente não precisa de nenhum hardware extra?",
          "Neste guia denso, ensinamos você a desapegar das velhas tecnologias de recepção. Explicamos por que o IPTV é imune à chuva, como ele entrega mais canais do que qualquer satélite e o que você deve configurar no seu roteador para que ele funcione como a 'melhor antena do mundo'.",
          "Prepare-se para limpar o seu telhado e simplificar sua sala, transformando sua internet na fonte definitiva de diversão 4K."
        ]}
        breadcrumbs={breadcrumbs}
        faq={faq}
        benefits={benefits}
        howItWorks={howItWorks}
        diferenciais={diferenciais}
        sections={sections}
        ctaTitle="📡 Cansado de Antenas que Saem do Ar com Chuva?"
        ctaDescription="A P2 PRO é 100% digital e imune ao tempo. Mude para a elite do streaming e tenha sinal liso 365 dias por ano."
        ctaButtonText="Testar Sem Antena"
      />
    </div>
  );
};

export const Route = createFileRoute("/blog/iptv-precisa-de-antena")({
  head: () => ({
    meta: [
      { title: "IPTV Precisa de Antena? Guia Completo e Técnico 2026" },
      { name: "description", content: "Descubra por que o IPTV não precisa de antena parabólica ou digital. Entenda a tecnologia via internet da P2 PRO e como ter sinal 4K estável sem cabos coaxiais." },
    ],
  }),
  component: ArticlePage,
});
